import { useState } from "react";
import { DayPicker, type Matcher } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import { CalendarDays, ChevronLeft, X } from "lucide-react";

import { usePopover } from "@/hooks/use-popover";
import { djs } from "@/lib/djs";
import { cn } from "@/lib/utils";

export interface DatePreset {
	label: string;
	days: number;
}

const DAY_PICKER_CLASS_NAMES = {
	months: "relative",
	month: "flex flex-col gap-2",
	month_caption: "relative flex h-8 items-center justify-center px-8",
	caption_label: "text-sm font-semibold capitalize text-foreground",
	nav: "absolute inset-x-0 top-0 flex items-center justify-between",
	button_previous: "control-button button-ghost size-9",
	button_next: "control-button button-ghost size-9",
	chevron: "size-4",
	month_grid: "w-full border-collapse",
	weekday: "size-9 text-xs font-medium capitalize text-muted-foreground",
	day: "p-0 text-center",
	day_button: "control-button size-9 text-sm text-foreground hover:bg-secondary",
	today: "[&_button]:font-semibold [&_button]:text-primary",
	selected: "[&_button]:bg-primary [&_button]:text-primary-foreground [&_button:hover]:bg-primary",
	disabled: "[&_button]:pointer-events-none [&_button]:text-muted-foreground",
	outside: "[&_button]:text-muted-foreground",
	hidden: "invisible",
};

export function DatePicker({
	value,
	onChange,
	disabled = false,
	presets,
	presetHeader = "Escolher data",
	placeholder = "Escolher data",
	clearable = false,
	minDate,
	maxDate,
	align = "left",
	className,
	containerClassName,
	slot,
}: {
	value: string | null;
	onChange: (value: string | null) => void;
	disabled?: boolean;
	presets?: DatePreset[];
	presetHeader?: string;
	placeholder?: string;
	clearable?: boolean;
	minDate?: Date;
	maxDate?: Date;
	align?: "left" | "right";
	className?: string;
	containerClassName?: string;
	slot?: string;
}) {
	const { open, setOpen, rootRef, panelRef } = usePopover<HTMLSpanElement>(align);

	const [picking, setPicking] = useState(!presets || presets.length === 0);

	const hasPresets = presets !== undefined && presets.length > 0;

	const commit = (next: string | null) => {
		onChange(next);
		setOpen(false);
		setPicking(!hasPresets);
		rootRef.current?.querySelector<HTMLElement>("button")?.focus();
	};

	const selected = value === null ? undefined : djs(value, "YYYY-MM-DD").toDate();

	const disabledDays: Matcher[] = [];

	if (minDate) {
		disabledDays.push({ before: minDate });
	}

	if (maxDate) {
		disabledDays.push({ after: maxDate });
	}

	return (
		<span ref={rootRef} className={cn("relative inline-flex", containerClassName)}>
			<button
				type="button"
				data-slot={slot}
				aria-expanded={open}
				disabled={disabled}
				onClick={() => {
					setOpen((current) => !current);
					setPicking(!hasPresets);
				}}
				className={cn(
					"control-button button-outline min-h-11 px-3 py-2 text-xs md:min-h-10",
					"hover:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
					"disabled:cursor-not-allowed disabled:opacity-50",
					value === null ? "text-muted-foreground" : "text-foreground",
					open && "bg-secondary text-foreground",
					className,
				)}
			>
				<CalendarDays className="size-3.5 shrink-0 text-muted-foreground" />
				{value === null && placeholder}
				{value !== null && djs(value, "YYYY-MM-DD").format("DD/MM/YYYY")}
			</button>

			{open && !picking && (
				<div
					ref={panelRef}
					popover="manual"
					data-component="date-picker-presets"
					className="anchored-popover min-w-52 p-1"
				>
					<p className="px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
						{presetHeader}
					</p>

					{presets?.map((preset) => {
						const date = djs().add(preset.days, "day").startOf("day");

						return (
							<button
								key={preset.label}
								type="button"
								data-slot="date-preset"
								disabled={disabled}
								onClick={() => commit(date.format("YYYY-MM-DD"))}
								className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-secondary focus-visible:bg-secondary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
							>
								{preset.label}
								<span className="flex shrink-0 items-center gap-1.5 font-mono text-xs text-muted-foreground">
									<span>{date.locale("pt-br").format("ddd")}</span>
									<span>{date.format("DD/MM")}</span>
								</span>
							</button>
						);
					})}

					<button
						type="button"
						data-slot="date-pick"
						disabled={disabled}
						onClick={() => setPicking(true)}
						className="mt-0.5 flex w-full items-center gap-2 border-t border-border px-2 py-1.5 pt-2 text-left text-xs font-medium text-primary transition-colors hover:bg-secondary focus-visible:bg-secondary focus-visible:outline-none"
					>
						<CalendarDays className="size-3.5 shrink-0" />
						Outra data
					</button>

					{clearable && value !== null && (
						<button
							type="button"
							data-slot="date-clear"
							disabled={disabled}
							onClick={() => commit(null)}
							className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:bg-secondary focus-visible:outline-none"
						>
							<X className="size-3.5 shrink-0" />
							Sem data
						</button>
					)}
				</div>
			)}

			{open && picking && (
				<div
					ref={panelRef}
					popover="manual"
					data-component="date-picker-calendar"
					className="anchored-popover p-2"
				>
					{hasPresets && (
						<button
							type="button"
							data-slot="date-back"
							onClick={() => setPicking(false)}
							className="mb-1 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:bg-secondary focus-visible:outline-none"
						>
							<ChevronLeft className="size-3.5" />
							presets
						</button>
					)}

					<DayPicker
						mode="single"
						locale={ptBR}
						selected={selected}
						defaultMonth={selected}
						disabled={disabledDays.length > 0 ? disabledDays : undefined}
						onSelect={(date) => {
							if (date) {
								commit(djs(date).format("YYYY-MM-DD"));
							}
						}}
						classNames={DAY_PICKER_CLASS_NAMES}
					/>

					{clearable && value !== null && (
						<button
							type="button"
							data-slot="date-clear"
							disabled={disabled}
							onClick={() => commit(null)}
							className="mt-1 flex w-full items-center gap-2 border-t border-border px-2 py-1.5 pt-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:bg-secondary focus-visible:outline-none"
						>
							<X className="size-3.5 shrink-0" />
							Sem data
						</button>
					)}
				</div>
			)}
		</span>
	);
}
